import React, { useState, useRef } from 'react';
import { AgencyService } from '../../services/supabase/agency.service';

interface PhotoUploadProps {
  userId: string;
  currentPhoto?: string | null;
  onPhotoUpdate: (photoUrl: string | null) => void;
  disabled?: boolean;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  userId,
  currentPhoto,
  onPhotoUpdate,
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentPhoto || null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Valider le fichier
    const validation = AgencyService.validateLogoFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Erreur de validation du fichier');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Utiliser la méthode simple directe en base64 pour la photo
      console.log('🎯 Upload photo direct en base de données...');
      const photoUrl = await AgencyService.uploadPhotoDirect(userId, file);
      
      if (!photoUrl) {
        throw new Error('Erreur lors de l\'upload de la photo');
      }

      // La photo est déjà sauvegardée en base
      onPhotoUpdate(photoUrl);
      setPreview(photoUrl);
      console.log('✅ Photo uploadée avec succès en base de données');
      
    } catch (error) {
      console.error('Erreur lors de l\'upload de la photo:', error);
      setError('Erreur lors de l\'upload de la photo');
    } finally {
      setIsUploading(false);
      // Réinitialiser l'input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    if (!currentPhoto) return;

    setIsUploading(true);
    setError(null);

    try {
      // Suppression directe en base de données
      const success = await AgencyService.removeUserPhoto(userId);
      
      if (success) {
        onPhotoUpdate(null);
        setPreview(null);
        console.log('✅ Photo supprimée avec succès');
      } else {
        setError('Erreur lors de la suppression de la photo');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      setError('Erreur lors de la suppression de la photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="space-y-4">
      {/* Zone d'upload */}
      <div 
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200
          ${disabled 
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
            : isUploading 
              ? 'border-blue-300 bg-blue-50' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }
        `}
        onClick={handleClick}
      >
        {/* Input file caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {/* Contenu */}
        <div className="flex flex-col items-center space-y-3">
          {/* Icône ou preview */}
          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="Photo de profil"
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="text-white text-sm">Chargement...</div>
                </div>
              )}
            </div>
          ) : (
            <div className={`
              w-24 h-24 rounded-full flex items-center justify-center
              ${isUploading ? 'bg-blue-100' : 'bg-gray-100'}
            `}>
              {isUploading ? (
                <div className="text-blue-600 text-sm">⏳</div>
              ) : (
                <div className="text-gray-400 text-2xl">👤</div>
              )}
            </div>
          )}

          {/* Texte */}
          <div>
            {isUploading ? (
              <p className="text-sm text-blue-600 font-medium">
                Upload en cours...
              </p>
            ) : preview ? (
              <p className="text-sm text-gray-600 font-medium">
                Cliquez pour changer la photo
              </p>
            ) : (
              <p className="text-sm text-gray-600 font-medium">
                Cliquez pour ajouter une photo
              </p>
            )}
          </div>

          {/* Bouton suppression */}
          {preview && !isUploading && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePhoto();
              }}
              className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
            >
              Supprimer la photo
            </button>
          )}
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Informations */}
      <div className="text-xs text-gray-500 space-y-1">
        <div>Formats acceptés: JPG, PNG, GIF, WebP, SVG</div>
        <div>Taille maximale: 10MB</div>
        <div>Optimisé pour: 200×200px (avatar)</div>
        <div className="text-green-600">⚡ Compression automatique inclus</div>
      </div>
    </div>
  );
};

export default PhotoUpload;
